<script setup lang="ts">
import { BForm, BFormGroup, BFormInput, BModal, BFormInvalidFeedback } from 'bootstrap-vue-next';
import { ref, computed, watch } from 'vue';

import { verifyGitlabIssue } from '~/api/manageIssue';
import LoadingButton from '~/common/components/LoadingButton.vue';
import { isValidGitLabIssueUrl, parseGitLabIssueUrl } from '~/common/IssueUrlRegexUtils';
import { useGitlabHostname } from '~/common/useGitlabHostname';

const CHECK_FAILURE_MESSAGES = {
  not_found: 'Issue not found. Please check the URL.',
  unreachable: 'Could not reach GitLab. Please try again.',
  unverifiable: 'Unable to verify issue. Please try again.',
};

const gitlabHostname = useGitlabHostname();

const props = defineProps<{
  currentIssueUrl: string;
}>();

const duplicateIssueReference = ref('');
const model = defineModel<boolean>();
const emit = defineEmits<{ confirm: [duplicateIssueReference: string] }>();
const loading = ref(false);
const apiErrorMessage = ref<string | null>(null);
const requestCounter = ref(0);

const isUrlFormatValid = computed(() => {
  const reference = duplicateIssueReference.value.trim();

  if (reference.length === 0) {
    return false;
  }

  if (reference === props.currentIssueUrl) {
    return false;
  }

  return isValidGitLabIssueUrl(reference, gitlabHostname);
});

const isValidated = computed(() => isUrlFormatValid.value && !apiErrorMessage.value);

const invalidFeedback = computed(() =>
  apiErrorMessage.value ?? 'Invalid issue URL format. Please check the URL.',
);

watch(duplicateIssueReference, () => {
  apiErrorMessage.value = null;
});

const handleDuplicateConfirm = async (): Promise<void> => {
  const reference = duplicateIssueReference.value.trim();
  const parsedUrl = parseGitLabIssueUrl(reference, gitlabHostname);

  if (!isUrlFormatValid.value || !parsedUrl) {
    return;
  }

  loading.value = true;
  apiErrorMessage.value = null;
  requestCounter.value += 1;
  const thisRequest = requestCounter.value;

  const check = await verifyGitlabIssue(parsedUrl.apiUrl);

  if (check === 'found') {
    emit('confirm', reference);
  }

  if (thisRequest === requestCounter.value) {
    loading.value = false;
    apiErrorMessage.value = check === 'found' ? null : CHECK_FAILURE_MESSAGES[check];
  }
};

const handleCloseDuplicateCancel = () => {
  duplicateIssueReference.value = '';
  apiErrorMessage.value = null;
};
</script>

<template>
  <BModal
    v-model="model"
    no-footer
    title="Close duplicate"
    @hidden="handleCloseDuplicateCancel"
  >
    <BForm @submit.prevent="handleDuplicateConfirm">
      <p>You are about to close this issue as a duplicate.</p>

      <BFormGroup
        label="Original issue URL:"
        label-for="duplicate-reference"
        :state="isValidated"
      >
        <BFormInput
          id="duplicate-reference"
          v-model="duplicateIssueReference"
          placeholder="Enter issue reference..."
          aria-describedby="duplicate-reference-feedback"
        />
        <BFormInvalidFeedback id="duplicate-reference-feedback">
          {{ invalidFeedback }}
        </BFormInvalidFeedback>
      </BFormGroup>

      <p class="text-muted small mt-2">
        This action will close the issue and mark it as a duplicate.
        You must provide a reference to the original issue URL to proceed.
      </p>
      <LoadingButton
        type="submit"
        class="float-end"
        variant="primary"
        :loading="loading"
        loading-text="Checking..."
      >
        Close duplicate
      </LoadingButton>
    </BForm>
  </BModal>
</template>
